import { combineReducers } from 'redux';
import audit from '@/audit/state/reducer';
import { auditStateToLoad, auditStateToSave } from '@/audit/state/state';
import auth from '@/users/state/reducer';
import catalogues from '@/datasets/catalogue/state/reducer';
import cohorts from '@/cohorts/state/reducer';
import collectionDatasets from '@/collections/datasets/state/reducer';
import collections from '@/collections/state/reducer';
import configurations from '@/configurations/state/reducer';
import datasets from '@/datasets/state/reducer';
import datasetCollections from '@/datasets/collections/state/reducer';
import datasetConditions from '@/datasets/conditions/state/reducer';
import datasetMarkdown from '@/datasets/markdown/state/reducer';
import datasetDataManagement from '@/datasets/data/state/reducer';
import datasetMetrics from '@/datasets/metrics/state/reducer';
import datasetNamespaces from '@/datasets/namespaces/state/reducer';
import datasetNotifications from '@/datasets/notifications/state/reducer';
import datasetReports from '@/datasets/reports/state/reducer';
import datasetDataModels from '@/datasets/datamodels/state/reducer';
import datasetDatasource from '@/datasets/datasource/state/reducer';
import dictionaryMarkdown from '@/datasets/dictionaries/markdown/state/reducer';
import datasetSettings from '@/datasets/settings/state/reducer';
import datasetUsers from '@/datasets/sharing/state/reducer';
import layout from '@/layout/state/reducer';
import metrics from '@/metrics/state/reducer';
import notifications from '@/notifications/state/reducer';
import pmnFunctions from '@/datasets/pmn-functions/state/reducer';
import requests from '@/requests/state/reducer';
import requestMarkdown from '@/requests/markdown/state/reducer';
import roles from '@/roles/state/reducer';
import search from '@/search/state/reducer';
import themes from '@/themes/state/reducer';
import tokens from '@/tokens/state/reducer';
import userGroups from '@/users/groups/state/reducer';
import vocabularies from '@/vocabularies/state/reducer';
import websocket from '@/websocket/state/reducer';
import workflows from '@/workflows/state/reducer';
import workspaces from '@/workspaces/state/reducer';
import { layoutStateToLoad, layoutStateToSave } from '@/layout/state/state';
import { themeStateToLoad, themeStateToSave } from '@/themes/state/state';
import { searchStateToLoad, searchStateToSave } from '@/search/state/state';
import { requestStateToLoad, requestStateToSave } from '@/requests/state/state';
import { userStateToLoad, userStateToSave } from '@/users/state/state';
import { roleStateToLoad, roleStateToSave } from '@/roles/state/state';
import { tokenStateToLoad, tokenStateToSave } from '@/tokens/state/state';
import {
  notificationsStateToLoad,
  notificationsStateToSave,
  notificationsSessionStateToSave,
} from '@/notifications/state/state';
import { datasetStateToLoad, datasetStateToSave } from '@/datasets/state/state';
import { cohortStateToLoad, cohortStateToSave } from '@/cohorts/state/state';
import { collectionStateToLoad, collectionStateToSave } from '@/collections/state/state';
import { userGroupsStateToLoad, userGroupsStateToSave } from '@/users/groups/state/state';
import { vocabularyStateToLoad, vocabularyStateToSave } from '@/vocabularies/state/state';
import app from './reducer';

const rootReducer = combineReducers({
  app,
  audit,
  auth,
  catalogues,
  cohorts,
  collectionDatasets,
  collections,
  configurations,
  datasetCollections,
  datasetConditions,
  datasetDataManagement,
  datasetDataModels,
  datasetDatasource,
  datasetMarkdown,
  datasetMetrics,
  datasetNamespaces,
  datasetNotifications,
  datasetReports,
  datasets,
  datasetSettings,
  datasetUsers,
  dictionaryMarkdown,
  layout,
  metrics,
  notifications,
  pmnFunctions,
  requestMarkdown,
  requests,
  roles,
  search,
  themes,
  tokens,
  userGroups,
  vocabularies,
  websocket,
  workflows,
  workspaces,
});

const saveLocally = (state) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('fair_layout_settings', serializedState);

    return true;
  } catch (e) {
    return false;
  }
};

export const saveSettings = (state) => {
  const stateToSave = {
    ...auditStateToSave(state),
    ...cohortStateToSave(state),
    ...collectionStateToSave(state),
    ...datasetStateToSave(state),
    ...layoutStateToSave(state),
    ...notificationsStateToSave(state),
    ...requestStateToSave(state),
    ...roleStateToSave(state),
    ...searchStateToSave(state),
    ...themeStateToSave(state),
    ...tokenStateToSave(state),
    ...userGroupsStateToSave(state),
    ...userStateToSave(state),
    ...vocabularyStateToSave(state),
  };
  saveLocally(stateToSave);
};

const saveSessionLocally = (state) => {
  try {
    const serializedState = JSON.stringify(state);
    sessionStorage.setItem('fair_layout_session_settings', serializedState);

    return true;
  } catch (e) {
    return false;
  }
};

export const saveSessionSettings = (state) => {
  const stateToSave = {
    ...notificationsSessionStateToSave(state),
  };
  saveSessionLocally(stateToSave);
};

export const loadSettings = () => {
  try {
    const serializedState = localStorage.getItem('fair_layout_settings');
    const serializedStateSession = sessionStorage.getItem('fair_layout_session_settings');

    if (!serializedState) {
      return undefined;
    }

    const settings = JSON.parse(serializedState);
    const settingsSession = JSON.parse(serializedStateSession);
    const sessionTrue = settingsSession ? settingsSession.notifications.announcements.all.hideForSession : false;

    const state = {
      audit: auditStateToLoad(settings),
      auth: userStateToLoad(settings),
      cohorts: cohortStateToLoad(settings),
      collections: collectionStateToLoad(settings),
      datasets: datasetStateToLoad(settings),
      layout: layoutStateToLoad(settings),
      notifications: notificationsStateToLoad(settings, sessionTrue),
      requests: requestStateToLoad(settings),
      roles: roleStateToLoad(settings),
      search: searchStateToLoad(settings),
      themes: themeStateToLoad(settings),
      tokens: tokenStateToLoad(settings),
      userGroups: userGroupsStateToLoad(settings),
      vocabularies: vocabularyStateToLoad(settings),
    };

    return state;
  } catch (err) {
    return undefined;
  }
};

export default rootReducer;
